import { jsPDF } from "jspdf";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { CircularProgress, Grid, Icon } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";
import { gql, useLazyQuery } from "@apollo/client";
import EmpleadoLayout from "components/LayoutContainers/EmpleadoLayout";
import boxShadows from "assets/theme/base/boxShadows";
import EditableText from "components/EditableText";
import PdfHeader from "../components/pdfHeader";
import { formatMoneyPunto } from "utils/formatters";
import { nombreCompraDetalle, unidadCompraDetalle } from "utils/presentacionCompra";

const textosIniciales = [
  " ", // Número
  " ", // Solicitud N°
  " ", // Expediente
  "--/--/--", // Fecha Evaluación
];

const tipoResponsables = [
  { id: 1, label: "IVA Responsable Inscripto" },
  { id: 2, label: "IVA Responsable no Inscripto" },
  { id: 3, label: "IVA no Responsable" },
  { id: 4, label: "IVA Sujeto Exento" },
  { id: 5, label: "Consumidor Final" },
  { id: 6, label: "Responsable Monotributo" },
  { id: 7, label: "Sujeto no Categorizado" },
  { id: 8, label: "Proveedor del Exterior" },
  { id: 9, label: "Cliente del Exterior" },
  { id: 10, label: "IVA Liberado – Ley Nº 19.640" },
  { id: 11, label: "IVA Responsable Inscripto – Agente de Percepción" },
  { id: 12, label: "Pequeño Contribuyente Eventual" },
  { id: 13, label: "Monotributista Social" },
  { id: 14, label: "Pequeño Contribuyente Eventual Social" },
];

const tipoDocumentos = [
  { id: 80, label: "CUIT" },
  { id: 86, label: "CUIL" },
  { id: 96, label: "DNI" },
]

const EvaluacionProcesoContratacion = () => {
  const documentsRef = useRef(null);
  const { id } = useParams();
  const { lg } = boxShadows;

  const [textArray, setTextArray] = useState(textosIniciales);
  const [editable, setEditable] = useState(true);

  const [data, setData] = useState(null);
  const [getPedido, { loading }] = useLazyQuery(
    gql`
      query getPedido($id: ID) {
        pedidosCompra(filter: { id: $id }) {
          id
          numeroExpediente
          licitacionFin
          detalles{
            id
            numero
            nombreCompra
            unidadNombre
            unidadAbreviatura
            requierePack
            unidadPack
            materiaPrima{
              id
              nombre
              nombreCompra
              tipoUnidad {
                id
                nombre
              }
            }
            cantidadVerificada
            observacion
            precioEstimado
            nombreServicio
            cotizaciones{
              id
              nombreAlternativo
              observacion
              precio
              materiaPrimaAlternativo
              cotizacion{
                id
                proveedor{
                  razonSocial
                  nombre
                  apellido
                }
              }
            }
          }
          cotizaciones{
            id
            proveedor{
              tipoProveedor
              razonSocial
              nombre
              apellido
              tipoDocumento
              tipoResponsable
              documento
              localidad{
                nombre
                provincia{
                  nombre
                }
              }
            }
          }
        }
      }
    `,
    {
      onCompleted: (data) => {
        if (!data?.pedidosCompra[0]) return;
        setData(data.pedidosCompra[0]);
      },
    }
  );

  useEffect(() => {
    if (id) {
      getPedido({ variables: { id } });
    }
  }, [id]);

  useEffect(() => {
    if(data){
      const newTextArray = [...textArray];
      newTextArray[0] = data.id;
      newTextArray[1] = data.id;
      setTextArray(newTextArray);
    }
  }, [data]);

  const handleGeneratePdf = async () => {
    setEditable(false);
    const document = documentsRef.current;
    const doc = new jsPDF({
      format: "a4",
      unit: "px",
      orientation: "landscape",
      hotfixes: ["px_scaling"],
      compress: true,
    });

    // Define el footer con el número de página
    const addFooter = (doc, currentPage, totalPages) => {
      doc.setFontSize(8);
      doc.text(`Página ${currentPage} de ${totalPages}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, {
        align: "center",
      });
    };

    // Genera el contenido principal en el PDF
    await doc.html(document, {
      margin: [40, 0, 40, 0],
      autoPaging: "text",
      callback: async function (doc) {
        const totalPages = doc.internal.getNumberOfPages();

        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          addFooter(doc, i, totalPages);
        }

        // Guarda el archivo PDF
        doc.save(`exd-pedidoNro-${data.id}-preAdjudicacionPorItem.pdf`);
      },
    });

    setEditable(true);
  };

  const handleTextChange = (e, index) => {
    const newTextArray = [...textArray];
    newTextArray[index] = e.target.innerText;
    setTextArray(newTextArray);
  };

  const licitacionFinalizada = data?.licitacionFin && new Date(data.licitacionFin).getTime() <= Date.now();

  if (!data) return (
    <EmpleadoLayout>
      <SoftBox display="flex" justifyContent="center" alignItems="center" height="100vh" flexDirection="column" gap={3}>
        {loading ? (<>
          <CircularProgress size={40} color="primary" />
          <SoftTypography variant="h5">Cargando</SoftTypography>
        </>) : (<>
          <Icon fontSize="large">search_off</Icon>
          <SoftTypography variant="h5">No se encontró el pedido</SoftTypography>
        </>)}
      </SoftBox>
    </EmpleadoLayout>
  );

 if (!licitacionFinalizada) {
    return (
      <EmpleadoLayout>
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="100vh" flexDirection="column" gap={3}>
          <Icon fontSize="large" color="warning">lock</Icon>
          <SoftTypography variant="h5" color="warning">
            Documento disponible cuando finalice el período de presentación de ofertas.
          </SoftTypography>
        </SoftBox>
      </EmpleadoLayout>
    );
  }
  
  return (
    <EmpleadoLayout>
      <SoftBox
        p={2}
        display="flex"
        alignItems="center"
        flexDirection="column"
        minHeight="100vh"
        height="100%"
      >
        <div ref={documentsRef}>
          <SoftBox
            sx={{
              position: "relative",
              overflow: "hidden",
              width: "1123px",
              backgroundColor: "#ffffff",
              boxShadow: lg,
              px: 6,
            }}
            pb={5}
          >
            {/* ACA VA TODO EL CODIGO */}
            <PdfHeader />

            <SoftBox display="flex" flexDirection="column" gap={2} lineHeight={1.5} fontSize="12px">
              <Grid
                container
                border="1pt solid black"
                sx={{ '& > *:not(:last-child)': { borderBottom: "1pt solid black" } }}
              >
                <Grid item xs={12} fontWeight="bold" px={35} py={1} textAlign="center">
                  INFORME DE EVALUACIÓN DE PROCESO DE CONTRATACIÓN N°:{" "}
                  <EditableText
                    tooltip="Número"
                    editable={editable}
                    content={textArray[0]}
                    handleOnBlur={(e) => handleTextChange(e, 0)}
                  />
                  {" "}CONCURSO DE PRECIOS OTROS ORGANISMOS
                </Grid>

                <Grid
                  item
                  container
                  xs={12}
                  sx={{
                    '& > *:not(:last-child)': { borderRight: "1pt solid black" },
                    '& > *:nth-child(odd)': { backgroundColor: "#f0f0f0", fontWeight: "bold" },
                  }}
                >
                  <Grid item xs={2} px={1}>Método de Adjudicación </Grid>
                  <Grid item xs={10} px={1}>POR ITEM INDIVIDUAL AL MENOR PRECIO OFERTADO</Grid>
                </Grid>

                <Grid
                  item
                  container
                  xs={12}
                  sx={{
                    '& > *:not(:last-child)': { borderRight: "1pt solid black" },
                    '& > *:nth-child(odd)': { backgroundColor: "#f0f0f0", fontWeight: "bold" },
                  }}
                >
                  <Grid item xs={2} px={1}>Solicitud N° </Grid>
                  <Grid item xs={2} px={1}>
                    <EditableText
                      tooltip="Solicitud N°"
                      editable={editable}
                      content={textArray[1]}
                      handleOnBlur={(e) => handleTextChange(e, 1)}
                    />
                  </Grid>
                  <Grid item xs={2} px={1}>Fecha Evaluación</Grid>
                  <Grid item xs={2} px={1}>
                    <EditableText
                      tooltip="Fecha Evaluación"
                      editable={editable}
                      content={textArray[3]}
                      handleOnBlur={(e) => handleTextChange(e, 3)}
                    />
                  </Grid>
                </Grid>

                <Grid
                  item
                  container
                  xs={12}
                  sx={{
                    '& > *:not(:last-child)': { borderRight: "1pt solid black" },
                    '& > *:nth-child(odd)': { backgroundColor: "#f0f0f0", fontWeight: "bold" },
                  }}>
                  <Grid item xs={2} px={1}>Unidad Solicitante </Grid>
                  <Grid item xs={10} px={1}>{"UNIVERSIDAD PROVINCIAL DE OFICIOS \"EVA PERON\""}</Grid>
                </Grid>
              </Grid>

              <SoftTypography variant="h6">Cotizaciones Evaluadas por Items</SoftTypography>

              <SoftBox>
                {data?.detalles.map((detalle, index) => (
                  <SoftBox key={index} border="1pt solid black" mb={2}>
                    {/* Encabezado de la tabla */}
                    <Grid
                      container
                      backgroundColor="#f0f0f0"
                      textAlign="center"
                      fontWeight="bold"
                      sx={{ 
                        '& > *': { 
                          borderRight: "1pt solid black",
                          borderBottom: "1pt solid black"
                        },
                        '& > *:last-child': { 
                          borderRight: "none"
                        }
                      }}
                    >
                      <Grid item xs={0.8} px={0.5} py={1}>Item</Grid>
                      <Grid item xs={2.5} px={0.5} py={1}>Descripción</Grid>
                      <Grid item xs={0.8} px={0.5} py={1}>Cantidad</Grid>
                      <Grid item xs={0.8} px={0.5} py={1}>Unidad</Grid>
                      <Grid item xs={0.6} px={0.5} py={1}>Rnk</Grid>
                      <Grid item xs={2.2} px={0.5} py={1}>Proveedor</Grid>
                      <Grid item xs={2.4} px={0.5} py={1}>Marca / Modelo / Observación</Grid>
                      <Grid item xs={1.2} px={0.5} py={1}>Monto Unitario</Grid>
                      <Grid item xs={0.7} px={0.5} py={1}>Total Cotizado</Grid>
                    </Grid>

                    {/* Fila principal del producto */}
                    <Grid 
                      container 
                      sx={{ 
                        '& > *': { 
                          borderRight: "1pt solid black",
                          borderBottom: detalle.cotizaciones.length > 1 ? "1pt solid black" : "none"
                        },
                        '& > *:last-child': { 
                          borderRight: "none"
                        }
                      }}
                    >
                      <Grid item xs={0.8} px={0.5} py={1} textAlign="center" fontWeight="bold" backgroundColor="#f9f9f9">
                        {detalle.numero}
                      </Grid>
                      <Grid item xs={2.5} px={0.5} py={1} backgroundColor="#f9f9f9">
                        {[nombreCompraDetalle(detalle), !detalle.requierePack && detalle.observacion].filter(Boolean).join(" ")}
                      </Grid>
                      <Grid item xs={0.8} px={0.5} py={1} textAlign="center" backgroundColor="#f9f9f9">
                        {detalle.cantidadVerificada}
                      </Grid>
                      <Grid item xs={0.8} px={0.5} py={1} textAlign="center" backgroundColor="#f9f9f9">
                        {unidadCompraDetalle(detalle)}
                      </Grid>
                      
                      {/* Primera cotización o fila vacía si no hay cotizaciones */}
                      {detalle.cotizaciones.length > 0 ? (
                        <>
                          <Grid item xs={0.6} px={0.5} py={1} textAlign="center">{1}</Grid>
                          <Grid item xs={2.2} px={0.5} py={1}>
                            {[
                              detalle.cotizaciones[0].cotizacion?.proveedor?.razonSocial,
                              detalle.cotizaciones[0].cotizacion?.proveedor?.nombre,
                              detalle.cotizaciones[0].cotizacion?.proveedor?.apellido
                            ].filter(e => e).join(" ")}
                          </Grid>
                          <Grid item xs={2.4} px={0.5} py={1}>
                            {[
                              detalle.cotizaciones[0].nombreAlternativo, 
                              detalle.cotizaciones[0].observacion, 
                              detalle.cotizaciones[0].materiaPrimaAlternativo ? "(Alternativo)" : null
                            ].filter(e => e).join(" ") || "-"}
                          </Grid>
                          <Grid item xs={1.2} px={0.5} py={1} textAlign="right">
                            $ {formatMoneyPunto(detalle.cotizaciones[0].precio)}
                          </Grid>
                          <Grid item xs={0.7} px={0.5} py={1} textAlign="right">
                            $ {formatMoneyPunto(detalle.cotizaciones[0].precio * detalle.cantidadVerificada)}
                          </Grid>
                        </>
                      ) : (
                        <>
                          <Grid item xs={0.6} px={0.5} py={1}>-</Grid>
                          <Grid item xs={2.2} px={0.5} py={1}>-</Grid>
                          <Grid item xs={2.4} px={0.5} py={1}>-</Grid>
                          <Grid item xs={1.2} px={0.5} py={1}>-</Grid>
                          <Grid item xs={0.7} px={0.5} py={1}>-</Grid>
                        </>
                      )}
                    </Grid>

                    {/* Filas adicionales para las demás cotizaciones */}
                    {detalle.cotizaciones.slice(1).map((cotizacion, cotizacionIndex) => (
                      <Grid 
                        key={`cotizacion-${cotizacionIndex + 1}`} 
                        container 
                        sx={{ 
                          '& > *': { 
                            borderRight: "1pt solid black",
                            borderBottom: cotizacionIndex === detalle.cotizaciones.length - 2 ? "none" : "1pt solid black"
                          },
                          '& > *:last-child': { 
                            borderRight: "none"
                          },
                          backgroundColor: (cotizacionIndex + 1) % 2 === 0 ? "#f0f0f0" : "white"
                        }}
                      >
                        {/* Columnas vacías para item, descripción, cantidad y unidad */}
                        <Grid item xs={0.8} px={0.5} py={1}></Grid>
                        <Grid item xs={2.5} px={0.5} py={1}></Grid>
                        <Grid item xs={0.8} px={0.5} py={1}></Grid>
                        <Grid item xs={0.8} px={0.5} py={1}></Grid>
                        
                        {/* Datos de la cotización */}
                        <Grid item xs={0.6} px={0.5} py={1} textAlign="center">{cotizacionIndex + 2}</Grid>
                        <Grid item xs={2.2} px={0.5} py={1}>
                          {[
                            cotizacion.cotizacion?.proveedor?.razonSocial,
                            cotizacion.cotizacion?.proveedor?.nombre,
                            cotizacion.cotizacion?.proveedor?.apellido
                          ].filter(e => e).join(" ")}
                        </Grid>
                        <Grid item xs={2.4} px={0.5} py={1}>
                          {[
                            cotizacion.nombreAlternativo, 
                            cotizacion.observacion, 
                            cotizacion.materiaPrimaAlternativo ? "(Alternativo)" : null
                          ].filter(e => e).join(" ") || "-"}
                        </Grid>
                        <Grid item xs={1.2} px={0.5} py={1} textAlign="right">
                          $ {formatMoneyPunto(cotizacion.precio)}
                        </Grid>
                        <Grid item xs={0.7} px={0.5} py={1} textAlign="right">
                          $ {formatMoneyPunto(cotizacion.precio * detalle.cantidadVerificada)}
                        </Grid>
                      </Grid>
                    ))}
                  </SoftBox>
                ))}
              </SoftBox>

              <SoftBox>
                <SoftBox fontWeight="bold" borderBottom="1pt solid black">Padron de Proveedores</SoftBox>{" "}
                {data?.cotizaciones.map((c, index) => (  
                  <SoftBox key={index}>
                    - <strong>{[c.proveedor.razonSocial, c.proveedor.nombre, c.proveedor.apellido].join(" ").toUpperCase()}</strong>
                    {" "}
                    {[
                      tipoDocumentos.find(t => t.id == c.proveedor.tipoDocumento)?.label || "Documento",
                      c.proveedor.documento,
                      c.proveedor.localidad?.nombre,
                      c.proveedor.localidad?.provincia?.nombre,
                      tipoResponsables.find(t => t.id == c.proveedor.tipoResponsable)?.label
                    ].filter(e => e).join(" ").toUpperCase()}
                  </SoftBox>
                ))}
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </div>
        <SoftBox
          hidden={!editable}
          variant="contained"
          bgColor="white"
          p={2}
          style={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            zIndex: 1000,
            boxShadow:
              "inset 0rem 0rem 0.0625rem 0.0625rem rgb(255 255 255 / 90%), 0rem 1.25rem 1.6875rem 0rem rgb(0 0 0 / 5%)",
            borderRadius: "16px",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: `saturate(200%) blur(${pxToRem(30)})`,
          }}
        >
          <Grid container spacing={2} justifyContent="flex-end" id="button">
            <Grid item>
              <SoftButton variant="gradient" color="error" onClick={handleGeneratePdf}>
                <Icon fontWeight="bold">picture_as_pdf</Icon>
                &nbsp;&nbsp;Descargar PDF
              </SoftButton>
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
    </EmpleadoLayout>
  );
};

export default EvaluacionProcesoContratacion;
